import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import Loader from "../../../../components/Loader";
import GModal from "../../../../components/Modal";
import { addDialToList } from "../../../../config/redux/slices/dialerSlice";
import useContactPicker from "../../../../hooks/useContactPicker";
import { showErrors } from "../../../../utils/ui";
import {
  validateCronJobExpression,
  validateMobile,
} from "../../../../utils/validations";
import CronJobInput from "../CronJobInput";
import {
  StyledContactCard,
  StyledDialAdditionForm,
  StyledSelectContactPicker,
  StyledSelectContactWrapper,
} from "./styles";
import { addDialContact } from "../../../../adapters";

const AddContactModal = ({ visible, setVisible }) => {
  const dispatch = useDispatch();
  const contactPicker = useContactPicker();
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [processing, setIsProcessing] = React.useState(false);
  const closeModal = () => {
    !processing && setVisible(false);
  };

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
    resetForm,
    setValues,
  } = useFormik({
    initialValues: {
      name: "",
      contact: "",
      cronJob: "0 0 * * *", // daily
      notifications: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Contact name is required"),
      contact: Yup.number()
        .test("mobile-validation", "Enter valid mobile number", validateMobile)
        .required("Contact mobile is required"),
      cronJob: Yup.string()
        .test(
          "expression-validation",
          "Enter valid linux expression",
          validateCronJobExpression
        )
        .required("Expression is required"),
      notifications: Yup.bool().optional(),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      try {
        const response = await addDialContact({
          name: values.name,
          contact: values.contact,
          cronExpression: values.cronJob,
          enableNotifications: values.notifications,
        });
        toast.success(response.message);
        dispatch(addDialToList(response.data));
        setVisible(false);
        resetForm();
      } catch (e) {
        showErrors(e);
      }
      setIsProcessing(false);
    },
  });

  const selectContact = async () => {
    try {
      const contacts = await contactPicker.selectContacts();
      if (!contacts?.length && !values.contact)
        throw new Error("Select at least one contact");
      setValues((prev) => ({
        ...prev,
        name: contacts[0].name[0],
        contact: contacts[0].tel[0],
      }));
      setMobileNumbers(contacts[0].tel ?? []);
    } catch (e) {
      showErrors(e);
    }
  };

  return (
    <GModal
      isOpen={visible}
      onClose={closeModal}
      title="Add New Contact"
      width="540px"
      primaryButton={{
        onClick: handleSubmit,
        disabled: processing,
        endIcon: <BiPlus />,
        text: "Add",
      }}
      secondaryButton={{
        onClick: closeModal,
      }}
    >
      {processing && <Loader absolute showText="Adding task.." />}
      <StyledDialAdditionForm>
        {contactPicker.isSupported ? (
          <StyledSelectContactWrapper>
            <StyledSelectContactPicker>
              <Typography>
                {values.name ? "Select other contact:" : "Select contact:"}
              </Typography>
              <Button
                size="small"
                color={values.name ? "secondary" : "info"}
                variant="text"
                onClick={selectContact}
              >
                Select
              </Button>
            </StyledSelectContactPicker>
            {values.name ? (
              <Card variant="outlined">
                <StyledContactCard>
                  <Typography variant="body1" fontWeight="bold">
                    {values.name}
                  </Typography>
                  <Select
                    value={values.contact ?? ""}
                    onChange={(e) => setFieldValue("contact", e.target.value)}
                    disabled={mobileNumbers.length === 1}
                    color="secondary"
                    variant="filled"
                    size="small"
                  >
                    {mobileNumbers.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledContactCard>
              </Card>
            ) : null}
          </StyledSelectContactWrapper>
        ) : (
          <>
            <TextField
              value={values.name}
              type="text"
              name="name"
              label="Name"
              color="secondary"
              placeholder="Enter contact name"
              error={!!(touched.name && errors.name)}
              helperText={touched.name && errors.name ? errors.name : ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <TextField
              value={values.contact}
              type="number"
              name="contact"
              color="secondary"
              label="Mobile Number"
              placeholder="Enter contact mobile"
              error={!!(touched.contact && errors.contact)}
              helperText={
                touched.contact && errors.contact ? errors.contact : ""
              }
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </>
        )}
        <CronJobInput
          value={values.cronJob ?? ""}
          error={errors.cronJob}
          setValue={(value) => setFieldValue("cronJob", value)}
          onBlur={() => setFieldTouched("cronJob", true)}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={values.enabledNotifications}
              onChange={(_, checked) => setFieldValue("notifications", checked)}
            />
          }
          label="Enable Notifications"
        />
      </StyledDialAdditionForm>
    </GModal>
  );
};

export default AddContactModal;
